import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Globe2Icon, type LucideIcon } from "lucide-react";
import type { IntegrationProvider } from "@shared/integrations/connections";
import type { IntegrationState } from "@shared/integrations/state";
import { BokioMark, FortnoxMark, OqtoMark, SpirisMark, VitecMark } from "@/components/icons/brand-marks";
import type { ReactNode } from "react";

/**
 * The niche, vertical integrations — the ones behind the "Integrations"
 * settings section.
 *
 * Deliberately NOT everything we connect to. Google, Microsoft, iCloud and MCP
 * stay as top-level sections: the first three are accounts almost every user
 * already has and recognises by name, and MCP is a general mechanism rather
 * than a vendor. Burying those behind a generic label would make common things
 * harder to find in order to tidy up rare ones.
 *
 * What belongs here is the long tail — Swedish accounting, property listings,
 * whatever a particular customer's trade needs. Those are individually
 * irrelevant to most users, there will be many of them, and each one added as
 * its own section would push General and Members further down a list nobody
 * wants to scan.
 *
 * `capability` mirrors SettingsCapability: a deployment without credentials for
 * a provider must not offer it at all, because a visible tile whose only button
 * 503s reads as broken rather than unconfigured.
 */

/** Anything that renders like an icon: a Lucide glyph today, a brand SVG later. */
export type IntegrationIcon = LucideIcon | ((props: { className?: string }) => ReactNode);

export interface IntegrationDef {
  readonly id: string;
  readonly label: MessageDescriptor;
  /**
   * The tile's mark.
   *
   * Typed as any icon-shaped component rather than `LucideIcon` so a vendor's
   * real logo can be dropped in without touching the registry's shape. Until
   * the official SVGs are added, each provider carries a DISTINCT Lucide icon —
   * the two shared `CalculatorIcon` and were indistinguishable in a list, which
   * is the thing worth fixing first.
   */
  readonly icon: IntegrationIcon;
  /** One line, shown on the tile. What the agent gains, not what the vendor is. */
  readonly blurb: MessageDescriptor;
  /** Deployment capability required, when the provider needs per-env credentials. */
  readonly capability?: "fortnox" | "bokio" | "oqto" | "spiris" | "vitec" | "apis";
  /**
   * The `integration_connections.provider` value this tile's connections are
   * stored under — what the index reads to list what is connected without
   * opening each tile. Usually the id; the "Other APIs" tile is the one that
   * differs, because the agent's own sign-ins are a provider (`oauth_api`)
   * named for the mechanism rather than the tile.
   */
  readonly provider: IntegrationProvider;
}

export const INTEGRATIONS = [
  {
    id: "fortnox",
    label: msg`Fortnox`,
    icon: FortnoxMark,
    blurb: msg`Swedish accounting — invoices, vouchers and reports, one Fortnox company at a time.`,
    capability: "fortnox",
    provider: "fortnox",
  },
  {
    id: "bokio",
    label: msg`Bokio`,
    icon: BokioMark,
    blurb: msg`Swedish accounting — invoices, supplier invoices and bookkeeping, one Bokio company at a time.`,
    capability: "bokio",
    provider: "bokio",
  },
  {
    id: "oqto",
    label: msg`Oqto`,
    icon: OqtoMark,
    blurb: msg`Swedish accounting — vouchers, customer and supplier invoices, one Oqto company at a time.`,
    capability: "oqto",
    provider: "oqto",
  },
  {
    id: "spiris",
    label: msg`Spiris`,
    icon: SpirisMark,
    blurb: msg`Swedish accounting — invoices, vouchers and supplier invoices in Spiris Bokföring & Fakturering, one company at a time.`,
    capability: "spiris",
    provider: "spiris",
  },
  {
    id: "vitec",
    label: msg`Vitec Mäklarsystem`,
    icon: VitecMark,
    blurb: msg`Swedish real estate — properties, contacts, viewings, meetings and reports from Express, one office at a time.`,
    capability: "vitec",
    provider: "vitec",
  },
  {
    id: "apis",
    label: msg`Other APIs`,
    icon: Globe2Icon,
    blurb: msg`Services your agent has signed in to for you. Choose where each sign-in may be used, or add your own app.`,
    capability: "apis",
    provider: "oauth_api",
  },
] as const satisfies readonly IntegrationDef[];

export type IntegrationId = (typeof INTEGRATIONS)[number]["id"];

/**
 * The same list, widened for runtime iteration.
 *
 * `as const satisfies` narrows each entry to its own literal type, so an entry
 * without `capability` genuinely has no such property and reading it off the
 * union is a type error. Iterate this view when you need the optional fields;
 * use INTEGRATIONS when you want the literal id union. (Same pattern, and same
 * reason, as the registries in @desk-schemas/features.)
 */
export const ALL_INTEGRATIONS: readonly IntegrationDef[] = INTEGRATIONS;

/**
 * One connection as the Integrations index lists it — the shape
 * `GET /api/instances/integrations` answers with, defined beside the registry
 * because it is the registry's id that links the line to its tile.
 */
export interface InstalledIntegration {
  /** The tile this connection is shown under: an `IntegrationDef.id`. */
  readonly integration: string;
  readonly connectionId: string;
  /** What a human calls it: a company, an office, a service name. */
  readonly label: string;
  readonly status: IntegrationState;
}

/** The integration a path segment names, or null when it names none. */
export const asIntegrationId = (value: string | undefined): IntegrationId | null =>
  INTEGRATIONS.find((i) => i.id === value)?.id ?? null;
